/**
 * Welcome discount popup.
 *
 * Vanilla JS, no dependencies. Lazy by design: the very first thing the
 * constructor does is check localStorage and bail out immediately if the
 * popup has no reason to show, before touching the DOM at all.
 *
 * NOTE on the countdown timer: it is a display-only urgency device, backed
 * by sessionStorage so it doesn't reset on every page navigation within the
 * same session. It is NOT tied to the discount's actual expiry -- the
 * discount itself has no end date. Don't wire this to real expiry logic
 * without also updating the discount in Shopify to match.
 */

const STORAGE_LAST_SHOWN = 'welcomePopup:lastShown';
const STORAGE_REDEEMED = 'welcomePopup:redeemed';
const SESSION_COUNTDOWN_START = 'welcomePopup:countdownStart';

class WelcomePopup {
  constructor(root) {
    this.root = root;
    this.config = this.parseConfig(root.dataset);

    // Already redeemed? Never show again, regardless of the frequency window.
    if (localStorage.getItem(STORAGE_REDEEMED) === 'true') return;

    // Within the "show once every X days" window? Skip.
    const lastShown = parseInt(localStorage.getItem(STORAGE_LAST_SHOWN), 10);
    if (lastShown) {
      const elapsedDays = (Date.now() - lastShown) / 86400000;
      if (elapsedDays < this.config.frequencyDays) return;
    }

    this.overlay = root.querySelector('[data-welcome-popup-overlay]');
    this.modal = root.querySelector('.welcome-popup__modal');
    this.stageCta = root.querySelector('[data-stage="cta"]');
    this.stageReveal = root.querySelector('[data-stage="reveal"]');
    this.revealBtn = root.querySelector('[data-welcome-popup-reveal]');
    this.copyBtn = root.querySelector('[data-welcome-popup-copy]');
    this.closeBtn = root.querySelector('[data-welcome-popup-close]');
    this.countdownEl = root.querySelector('[data-welcome-popup-countdown]');
    this.couponEl = root.querySelector('.welcome-popup__coupon');

    this.bindEvents();
    this.timer = setTimeout(() => this.open(), this.config.delay);
  }

  parseConfig(d) {
    return {
      delay: parseInt(d.delay, 10) || 3000,
      frequencyDays: parseFloat(d.frequencyDays) || 7,
      code: d.code || '',
      escapeKey: d.escapeKey === 'true',
      clickOutside: d.clickOutside === 'true',
      confetti: d.confetti === 'true',
      countdown: d.countdown === 'true',
      countdownMinutes: parseInt(d.countdownMinutes, 10) || 10,
    };
  }

  bindEvents() {
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());

    if (this.config.clickOutside && this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    if (this.config.escapeKey) {
      this.escapeHandler = (event) => {
        if (event.key === 'Escape' && this.root.classList.contains('is-open')) this.close();
      };
      document.addEventListener('keydown', this.escapeHandler);
    }

    if (this.revealBtn) this.revealBtn.addEventListener('click', () => this.reveal());
    if (this.copyBtn) this.copyBtn.addEventListener('click', () => this.copyAndApply());
  }

  open() {
    this.root.hidden = false;
    localStorage.setItem(STORAGE_LAST_SHOWN, String(Date.now()));

    // Force layout so the opening transition actually runs.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.root.classList.add('is-open'));
    });

    document.body.classList.add('overflow-hidden');
    if (this.modal) this.modal.focus?.();
  }

  close() {
    this.root.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    setTimeout(() => {
      this.root.hidden = true;
    }, 500);
  }

  reveal() {
    if (this.stageCta) this.stageCta.hidden = true;
    if (this.stageReveal) this.stageReveal.hidden = false;

    if (this.config.confetti) this.launchConfetti();
    if (this.config.countdown) this.startCountdown();
  }

  launchConfetti() {
    if (!this.couponEl) return;
    const colors = ['#e8590c', '#ffd43b', '#1aa36b', '#1a1a1a', '#e64980'];
    const container = document.createElement('div');
    container.className = 'welcome-popup__confetti';

    for (let i = 0; i < 36; i++) {
      const piece = document.createElement('span');
      piece.className = 'welcome-popup__confetti-piece';
      piece.style.setProperty('--x', `${Math.random() * 100}%`);
      piece.style.setProperty('--delay', `${Math.random() * 250}ms`);
      piece.style.setProperty('--duration', `${900 + Math.random() * 700}ms`);
      piece.style.setProperty('--confetti-color', colors[i % colors.length]);
      container.appendChild(piece);
    }

    this.couponEl.appendChild(container);
    setTimeout(() => container.remove(), 2000);
  }

  startCountdown() {
    if (!this.countdownEl) return;

    let startedAt = parseInt(sessionStorage.getItem(SESSION_COUNTDOWN_START), 10);
    if (!startedAt) {
      startedAt = Date.now();
      sessionStorage.setItem(SESSION_COUNTDOWN_START, String(startedAt));
    }

    const totalMs = this.config.countdownMinutes * 60000;

    const tick = () => {
      const remaining = Math.max(0, totalMs - (Date.now() - startedAt));
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      this.countdownEl.textContent = `Offer reserved for ${minutes}:${String(seconds).padStart(2, '0')}`;
      if (remaining <= 0) clearInterval(this.countdownInterval);
    };

    tick();
    this.countdownInterval = setInterval(tick, 1000);
  }

  async copyAndApply() {
    if (!this.config.code) return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(this.config.code);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = this.config.code;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
    } catch (e) {
      // Clipboard can fail (permissions, insecure context); the discount
      // still gets applied via the redirect below regardless, so this is
      // non-fatal -- the customer just won't have it on their clipboard.
    }

    localStorage.setItem(STORAGE_REDEEMED, 'true');

    const label = this.copyBtn.querySelector('.welcome-popup__copy-label');
    const success = this.copyBtn.querySelector('.welcome-popup__copy-success');
    this.copyBtn.classList.add('is-success');
    if (label) label.hidden = true;
    if (success) success.hidden = false;

    const redirectTo = window.location.pathname + window.location.search;

    setTimeout(() => {
      this.close();
      window.location.href = `/discount/${encodeURIComponent(this.config.code)}?redirect=${encodeURIComponent(redirectTo)}`;
    }, 700);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const root = document.querySelector('[data-welcome-popup-root]');
  if (root) new WelcomePopup(root);
});
